import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthProvider';
import { supabase } from '@/utils/supabaseClient';
import designTokens from '@/design-tokens';
import { useResponsive } from '@/contexts/ResponsiveContext';
import { PrimaryButton } from '@/components/ui/Buttons/Primary';
import { TextButton } from '@/components/ui/Buttons/Text';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import { Lock } from '@/components/ui/Illustrations/Lock';
import { DashboardPreview } from './DashboardPreview';

type GateType = 'trial_expired' | 'cancelled' | 'past_due';

interface SubscriptionGateProps {
  type: GateType;
  onAction?: () => void;
}

const TITLE_CONFIG: Record<GateType, string> = {
  trial_expired: 'Your Free Trial Has Ended',
  cancelled: 'Subscription Canceled',
  past_due: 'Payment Past Due',
};

const OWNER_CONFIG: Record<GateType, {
  message: string;
  buttonText: string;
}> = {
  trial_expired: {
    message: 'Subscribe to a plan to pick up right where you left off. Your data is safe and waiting for you.',
    buttonText: 'Choose a Plan',
  },
  cancelled: {
    message: 'Your subscription has been canceled. Reactivate to regain access. Your data is safe and waiting for you.',
    buttonText: 'Update Payment',
  },
  past_due: {
    message: 'Update your payment method to restore access. Your data is safe and waiting for you.',
    buttonText: 'Update Payment',
  },
};

const getMemberMessage = (type: GateType, ownerInfo: { name: string; email: string } | null): { prefix: string; suffix: string } => {
  const base = {
    trial_expired: 'Your team\'s free trial has ended.',
    cancelled: 'Your team\'s subscription has been canceled.',
    past_due: 'Your team\'s payment is past due.',
  };
  const contactMsg = ownerInfo
    ? `Reach out to ${ownerInfo.name} at ${ownerInfo.email} to restore access.`
    : 'Contact your account owner to restore access.';
  return { prefix: base[type], suffix: contactMsg };
};

export const SubscriptionGate: React.FC<SubscriptionGateProps> = ({ type, onAction }) => {
  const navigate = useNavigate();
  const { isOwner, signOut, userProfile } = useAuth();
  const { isMobile } = useResponsive();
  const owner = isOwner();

  const [ownerInfo, setOwnerInfo] = useState<{ name: string; email: string } | null>(null);

  useEffect(() => {
    if (owner || !userProfile?.tenant_id) return;

    const fetchOwner = async () => {
      const { data } = await supabase
        .from('users')
        .select('first_name, last_name, email')
        .eq('tenant_id', userProfile.tenant_id)
        .eq('role', 'owner')
        .limit(1)
        .single();

      if (data) {
        const name = [data.first_name, data.last_name].filter(Boolean).join(' ');
        setOwnerInfo({ name: name || 'your account owner', email: data.email });
      }
    };

    fetchOwner();
  }, [owner, userProfile?.tenant_id]);

  const handlePrimaryAction = () => {
    if (owner) {
      if (onAction) {
        onAction();
      } else {
        navigate('/settings/subscription');
      }
    } else {
      signOut();
    }
  };

  const handleSignOut = () => {
    signOut();
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: designTokens.surface?.greyscale?.fog || '#f9fafb',
      position: 'relative'
    }}>
      {/* Dashboard Preview Background */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <DashboardPreview />
      </div>

      {/* Overlay */}
      <Overlay isOpen={true} />

      {/* Modal Card */}
      <div style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
        padding: isMobile ? '1rem' : '2rem',
        zIndex: 99999,
        pointerEvents: 'none'
      }}>
        <div style={{
          backgroundColor: designTokens.surface?.greyscale?.clear || '#ffffff',
          borderRadius: designTokens.radius?.lgr || '24px',
          padding: '48px',
          boxShadow: 'none',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          pointerEvents: 'auto',
          maxWidth: '480px',
          width: '100%',
        }}>

          {/* Illustration */}
          <div style={{ marginBottom: '1.5rem' }}>
            <Lock width={160} height={170} />
          </div>

          {/* Title */}
          <h2 style={{
            fontFamily: designTokens['font/family/bold'] || 'Montserrat, system-ui, sans-serif',
            fontWeight: designTokens['font/weight/bold'] || '700',
            fontSize: designTokens['font/size/xl'] || '1.25rem',
            lineHeight: designTokens['font/line-height/lg'] || '1.75rem',
            color: designTokens['text/primary'] || '#000000',
            margin: 0,
            marginBottom: '0.75rem'
          }}>
            {TITLE_CONFIG[type]}
          </h2>

          {/* Message */}
          {owner ? (
            <p style={{
              fontSize: designTokens['font/size/md'] || '1rem',
              color: designTokens['text/primary'] || '#000000',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              maxWidth: '380px',
              fontFamily: 'Montserrat, system-ui, sans-serif'
            }}>
              {OWNER_CONFIG[type].message}
            </p>
          ) : (
            <p style={{
              fontSize: designTokens['font/size/md'] || '1rem',
              color: designTokens['text/primary'] || '#000000',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              maxWidth: '380px',
              fontFamily: 'Montserrat, system-ui, sans-serif'
            }}>
              {getMemberMessage(type, ownerInfo).prefix}{' '}
              {ownerInfo ? (
                <>
                  Reach out to <span style={{ fontWeight: designTokens['font/weight/semibold'] || '600' }}>{ownerInfo.name}</span> at{' '}
                  <a href={`mailto:${ownerInfo.email}`} style={{
                    color: designTokens.surface?.brand?.primary || '#2bb673',
                    textDecoration: 'none',
                    fontWeight: designTokens['font/weight/medium'] || '500'
                  }}>{ownerInfo.email}</a> to restore access.
                </>
              ) : (
                'Contact your account owner to restore access.'
              )}
            </p>
          )}

          {/* Primary Action */}
          <PrimaryButton
            onClick={handlePrimaryAction}
            style={{
              fontSize: designTokens.font?.size?.lg || '1.125rem',
              padding: '1rem 2rem'
            }}
          >
            {owner ? OWNER_CONFIG[type].buttonText : 'Sign Out'}
          </PrimaryButton>

          {/* Secondary: sign out for owner */}
          {owner && (
            <div style={{ marginTop: '1rem' }}>
              <TextButton size="small" onClick={handleSignOut}>
                Sign in with a different account
              </TextButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
